'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackMetaPixel } from '@/lib/analytics/meta-pixel'

/**
 * Meta Pixel SPA page-view tracking.
 *
 * The optional-cookie bootstrap fires `PageView` once for the initial document.
 * App Router client-side navigations never re-execute it, so this component
 * records subsequent page views.
 */
export function MetaPixel() {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pixelId || !pathname) return
    const search = searchParams?.toString()
    const fullPath = search ? `${pathname}?${search}` : pathname

    // First run is the initial document — the install snippet already fired its
    // PageView, so record the path and bail rather than double-counting it.
    if (lastPathRef.current === null) {
      lastPathRef.current = fullPath
      return
    }

    // Guard against re-renders that don't actually change the URL (searchParams
    // identity can churn) turning into duplicate pixel hits.
    if (lastPathRef.current === fullPath) return
    lastPathRef.current = fullPath

    trackMetaPixel('PageView')
  }, [pixelId, pathname, searchParams])

  return null
}
